import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { detectCountryBlocking } from './countryBlockingDetector'
import { mapMessageToTranslationKey } from '../utils/apiMessageMapper'
import type {
  UserProfile,
  AuthResponse,
  LoginInput,
  RegisterInput,
  RegisterResponse,
  Room,
  RoomWithRound,
  Round,
  RoundAudit,
  SeatWithPlayer,
  UserParticipation,
  UserRound,
  ApiResponse,
  Pagination,
  Currency,
  CurrencyWithNetworks,
  UserBalance,
  Chain,
  UserUpdateInput,
  UserStatus,
  Config,
  AdminConfig,
  ConfigUpdateInput,
  CountryBlockingStats,
  ReferralStats
} from '@/types'
import i18n from '@/i18n';

const API_URL = "/api"

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  }
})

// Request interceptor to add auth token and language header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Add Accept-Language header based on current i18n language
  const currentLanguage = localStorage.getItem('i18nextLng') || 'en'
  config.headers['Accept-Language'] = currentLanguage

  return config
})

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];


const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Helper function to process API response messages
const processApiMessage = (data: any) => {
  if (data && typeof data.message === 'string') {
    const translationKey = mapMessageToTranslationKey(data.message);
    if (translationKey) {
      // Add translation metadata while preserving original message
      data._translationKey = translationKey;
      data._originalMessage = data.message;
      data._translation = i18n.t(translationKey as any);
    }
  }
  return data;
};

api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Process success response messages
    if (response.data) {
      processApiMessage(response.data);
    }
    return response;
  },
  (error: AxiosError) => {
    // Process error response messages
    if (error.response?.data) {
      processApiMessage(error.response.data);
    }
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Check for country blocking first
    if (detectCountryBlocking(error)) {
      // Country is blocked - don't retry or attempt refresh
      return Promise.reject(error);
    }

    // Only retry if 401, and not for the refresh token request itself
    if (
      error.response?.status === 401 &&
      (error.response?.data as any)?.message !== 'Email not verified' &&
      (error.response?.data as any)?.message !== 'Invalid credentials' &&
      !originalRequest._retry &&
      !(originalRequest.url && originalRequest.url.endsWith('/auth/refresh-token'))
    ) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (!originalRequest.headers) originalRequest.headers = {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        api.post('/auth/refresh-token', {}, { withCredentials: true })
          .then((response) => {
            const newToken = response.data.data.token;
            localStorage.setItem('auth_token', newToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            processQueue(null, newToken);
            if (!originalRequest.headers) originalRequest.headers = {};
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            localStorage.removeItem('auth_token')
            // window.location.href = '/login';
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  login: (data: LoginInput): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
    api.post('/auth/login', data),

  register: (data: RegisterInput): Promise<AxiosResponse<ApiResponse<RegisterResponse>>> =>
    api.post('/auth/register', data),

  resendVerificationEmail: (email: string): Promise<AxiosResponse<void>> =>
    api.post('/auth/resend-verification-email', { email }),

  refreshToken: (): Promise<AxiosResponse<ApiResponse<{ token: string }>>> =>
    api.post('/auth/refresh-token', {}, { withCredentials: true }),

  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/auth/revoke-token'),

  forgotPassword: (email: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (data: { token: string; password: string; passwordConfirm: string }): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.post('/auth/reset-password', data),
}

// User endpoints
export const userApi = {
  getStatus: (): Promise<AxiosResponse<ApiResponse<UserStatus>>> =>
    api.get('/users/status'),

  getCurrentUser: (): Promise<AxiosResponse<ApiResponse<{ user: UserProfile }>>> =>
    api.get('/users/me'),

  updateProfile: (data: UserUpdateInput): Promise<AxiosResponse<ApiResponse<{ user: UserProfile }>>> =>
    api.patch('/users/me', data),

  getUserRounds: (params?: { status?: string; page?: number; limit?: number }): Promise<AxiosResponse<ApiResponse<{ rounds: UserRound[], pagination: Pagination }>>> =>
    api.get('/rooms/user/rounds', { params }),

  getUserWins: (params?: { timeframe?: string; page?: number; limit?: number }): Promise<AxiosResponse<ApiResponse<{ rounds: Partial<Round>[], totalWinnings: string, pagination: Pagination }>>> =>
    api.get('/rooms/user/wins', { params }),

  getUserBalanceList: (): Promise<AxiosResponse<ApiResponse<{ balanceList: UserBalance[] }>>> =>
    api.get('/users/me/balance'),

  getUserCurrencyBalance: (currencyCode: string, chainId?: string): Promise<AxiosResponse<ApiResponse<{ balance: UserBalance }>>> => {
    const params = chainId ? { chainId } : {};
    return api.get(`/users/me/balance/${currencyCode}`, { params });
  },

  getDepositAddress: (chainId: string, currencyCode: string): Promise<AxiosResponse<ApiResponse<{ depositAddress: string, minDepositAmount: string }>>> =>
    api.get(`/users/me/deposit-address/${chainId}/${currencyCode}`),

  applyWithdrawal: (data: {
    currencyCode: string;
    chain: string;
    address: string;
    memo?: string;
    amount: string;
  }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post('/users/me/withdraw', data),

  getWithdrawalEligibility: (): Promise<AxiosResponse<ApiResponse<{
    eligibility: {
      canWithdraw: boolean;
      reason: string;
      lastWithdrawalDate: string | null;
      totalWins: number;
      winsSinceLastWithdrawal: number;
    }
  }>>> =>
    api.get('/users/me/withdraw/eligibility'),

  getUserTransactions: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    transactions: any[];
    pagination: {
      page: number;
      limit: number;
      hasMore: boolean;
    }
  }>>> =>
    api.get('/users/me/transactions', { params }),

  getUserStats: (days?: number): Promise<AxiosResponse<ApiResponse<{
    stats: {
      period: {
        days: number;
        from: string;
        to: string;
      };
      totalDeposits: string;
      totalWithdrawals: string;
      totalBets: string;
      totalWinnings: string;
      totalReferralRewards: string;
      netProfit: string;
      gamesPlayed: number;
      gamesWon: number;
      winRate: number;
    }
  }>>> =>
    api.get('/users/me/stats', { params: { days } }),
}

// Room endpoints
export const roomApi = {
  getAllRooms: (): Promise<AxiosResponse<ApiResponse<RoomWithRound[]>>> =>
    api.get('/rooms'),

  getRoomById: (id: number): Promise<AxiosResponse<ApiResponse<Room>>> =>
    api.get(`/rooms/${id}`),

  createRoom: (data: Partial<Room>): Promise<AxiosResponse<ApiResponse<Room>>> =>
    api.post('/rooms', data),

  updateRoom: (id: number, data: Partial<Room>): Promise<AxiosResponse<ApiResponse<Room>>> =>
    api.put(`/rooms/${id}`, data),

  deleteRoom: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rooms/${id}`),

  getCurrentRound: (roomId: number, signal?: AbortSignal): Promise<AxiosResponse<ApiResponse<Round>>> =>
    api.get(`/rooms/${roomId}/current-round`, { signal }),

  getRoundHistory: (roomId: number, params?: { page?: number; limit?: number }) =>
    api.get(`/rooms/${roomId}/rounds/history`, { params }),

  getRoundSeats: (roundId: number, signal?: AbortSignal): Promise<AxiosResponse<ApiResponse<SeatWithPlayer[]>>> =>
    api.get(`/rooms/rounds/${roundId}/seats`, { signal }),

  joinRound: (roundId: number, data: { amount: number; seatNumber?: number }) =>
    api.post(`/rooms/rounds/${roundId}/join`, data),

  getUserParticipation: (roomId: number, signal?: AbortSignal): Promise<AxiosResponse<ApiResponse<UserParticipation>>> =>
    api.get(`/rooms/${roomId}/participation`, { signal }),

  getRoundAudit: (roundId: number): Promise<AxiosResponse<ApiResponse<{ audit: RoundAudit }>>> =>
    api.get(`/rooms/rounds/${roundId}/audit`),
}

// Currency endpoints
export const currencyApi = {
  getCurrencyList: (): Promise<AxiosResponse<ApiResponse<{ currencies: Currency[] }>>> =>
    api.get('/currencies'),

  getCurrency: (code: string): Promise<AxiosResponse<ApiResponse<{ currency: CurrencyWithNetworks }>>> =>
    api.get(`/currencies/${code}`),

  getChainList: (): Promise<AxiosResponse<ApiResponse<{ chains: Chain[] }>>> =>
    api.get('/currencies/chains'),

  getWithdrawalFee: (coinId: number, chain: string): Promise<AxiosResponse<ApiResponse<{ fee: string }>>> =>
    api.get(`/currencies/withdrawal-fee`, { params: { coinId, chain } }),
}

// Config endpoints
export const configApi = {
  getConfig: (): Promise<AxiosResponse<ApiResponse<Config>>> =>
    api.get('/config'),

  getAdminConfig: (): Promise<AxiosResponse<ApiResponse<AdminConfig>>> =>
    api.get('/config/admin'),

  updateAdminConfig: (data: ConfigUpdateInput): Promise<AxiosResponse<ApiResponse<AdminConfig>>> =>
    api.patch('/config/admin', data),
}

// Referral endpoints
export const referralApi = {
  getReferralCode: (): Promise<AxiosResponse<ApiResponse<{ referralCode: string }>>> =>
    api.get('/referrals/code'),

  getReferralLink: (): Promise<AxiosResponse<ApiResponse<{ referralLink: string; referralCode: string }>>> =>
    api.get('/referrals/link'),

  getReferralStats: (): Promise<AxiosResponse<ApiResponse<ReferralStats>>> =>
    api.get('/referrals/stats'),

  getReferredUsers: (params: { limit?: number; offset?: number }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/referrals/users', { params }),

  validateReferralCode: (code: string): Promise<AxiosResponse<ApiResponse<{ valid: boolean; referrer?: any; message?: string }>>> =>
    api.post('/referrals/validate', { code }),

  getReferralProgramStatus: (): Promise<AxiosResponse<ApiResponse<{ active: boolean; message: string }>>> =>
    api.get('/referrals/status'),
}

// Admin Country Blocking endpoints
export const adminCountryBlockingApi = {
  getBlockedCountries: (): Promise<AxiosResponse<ApiResponse<{ blockedCountries: string[] }>>> =>
    api.get('/admin/blocked-countries'),

  addBlockedCountry: (countryCode: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.post('/admin/blocked-countries', { countryCode }),

  removeBlockedCountry: (countryCode: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete('/admin/blocked-countries', { data: { countryCode } }),

  setBlockedCountries: (countries: string[]): Promise<AxiosResponse<ApiResponse<{ blockedCountries: string[] }>>> =>
    api.put('/admin/blocked-countries', { countries }),

  getCountryBlockingStats: (): Promise<AxiosResponse<ApiResponse<{ cacheStats: CountryBlockingStats }>>> =>
    api.get('/admin/blocked-countries/stats'),
}

export default api 